import React, { useState } from 'react';
import styled from '@emotion/styled';
import { graphql, StaticQuery } from 'gatsby';
import { Inner, gridColumns } from 'styles/structure';
import colors from 'styles/colors';
import dimensions from 'styles/dimensions';
import CleanInput from 'components/_ui/CleanInput';
import Button from 'components/_ui/Button';
import { window } from 'browser-monads';
import Recaptcha from 'react-recaptcha';
import Content from 'components/_ui/Content';

const FormContainer = styled.div`
  background: transparent;
  padding: 6.4rem 0 8rem;
  
  &.is-dark {
    background: ${colors.orange200};
  }

  header {
    text-align: center;

    p {
      margin-top: 1.6rem;
    }

    & + * {
      margin-top: 4rem;
    }
  }

  input {
    width: 100%;
    background-color: #fff;
    border: 1px solid ${colors.grey500};
    padding: 1.8rem 2rem 1.3rem;
    color: ${colors.teal800};
    font-size: 1.4rem;
    line-height: 1.33;

    &:focus {
      border-color: ${colors.red500};
      transition: border 0.3s ease-in-out;
    }
  }

  label {
    display: block;
    color: ${colors.teal800};
    font-size: 1.4rem;
    margin-top: 1.2rem;
  }

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    header {
      & + * {
        margin-top: 6.4rem;
      }
    }
  }
`;

const FormWrap = styled.div`
  .CondensedForm--landing & {
    > div {
      & + * {
        margin-top: 4.8rem;
      }
    }
  }

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    .CondensedForm--landing & {
      display: flex;
      align-items: flex-start;
      
      > div {
        flex: 1 0 40%;
        
        & + * {
          margin-top: 0;
          flex: 2 1 60%;
          padding-left: 6.4rem;
        }
      }
    }
  }
`;

const FormFields = styled.div`
  display: block;
  width: 100%;

  > div {
    & + * {
      margin-top: 3.2rem;
    }
  }

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    display: flex;
    flex-wrap: wrap;
    
    > div {
        ${gridColumns('1/2', 3.2)}
  
        & + * {
          margin-top: 0;
        }
  
        &:nth-of-type(n + 3) {
          margin-top: 3.2rem;
        }
  
        &:nth-of-type(even) {
          margin-left: 3.2rem;
        }
      }
  }
`;

const FormCloak = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
`;

const SelectField = styled.div`
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    right: 1rem;
    width: 0;
    height: 0;
    border-style: solid;
  }

  // up triangle
  &::before {
    top: 1.6rem;
    border-width: 0 6px 8px 6px;
    border-color: transparent transparent ${colors.teal700} transparent;
  }

  // down triangle
  &::after {
    top: 2.8rem;
    border-width: 8px 6px 0 6px;
    border-color: ${colors.teal700} transparent transparent transparent;
  }

  select {
    cursor: pointer;
    display: block;
    appearance: none;
    border-radius: 0;
    width: 100%;
    background-color: #fff;
    border: 1px solid ${colors.grey500};
    padding: 1.6rem 2rem;
    color: ${colors.teal800};
    font-size: 1.4rem;
    line-height: 1.33;

    &:focus {
      border-color: ${colors.red500};
      transition: border 0.3s ease-in-out;
    }
  }
`;

const FormButton = styled.div`
  margin-top: 5.6rem;
  text-align: center;
  
  > * {
    width: 100%;

    & + * {
      margin-top: 2.4rem;
    }
  }

  @media (min-width: ${dimensions.tabletPortraitUp}px) {
    > * {
      width: auto;
    }
  }

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    display: flex;
    justify-content: center;
    align-items: center;

    .has-content & {
      justify-content: flex-start;
    }
  
    > * {
      & + * {
        margin-top: 0;
        margin-left: 2rem;
      }
    }
  }
`;

const ButtonRecaptchaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: space-between;
  margin: auto;
  align-items: center;
  padding: 5rem 0;
  
  .has-content & {
    align-items: flex-start;
  }
`;

const CondensedContactForm = props => {
  const [submitted] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [hasError] = useState(false);
  const [errors] = useState(false);
  const [button, setButton] = useState('button');

  const success = window.location.search === '?thanks';

  const handleSubmit = e => {
    e.preventDefault();

    window.dataLayer.push({
      event: `${props.variant}-form-tracking`,
      eventCategory: props.category,
    });

    console.log('%c GTM dataLayer sent', 'font-weight: bold; color: grey');

    e.target.submit();
  };

  const recaptchaLoaded = () => {
    console.log('%c ReCaptcha loaded', 'font-weight: bold; color: green');
  };

  const verifyCallback = resp => {
    if (resp) {
      setIsVerified(true);
      setButton('submit');
    }
  };

  const verifyYouAreHumanMsg = () => {
    if (!isVerified) {
      alert('Please verify you are human.');
    }
  };

  return (
    <FormContainer className={`${props.variant ? `CondensedForm--${props.variant}` : ''} ${(props.content && props.content[0].length > 0) ? 'has-content' : ''} ${props.background ? `is-${props.background.toLowerCase()}` : ''}`}>
      <Inner>
        {props.heading && (
          <header>
            <h3>{success ? 'Thank You' : props.heading}</h3>

            {success && props.thankYou && <p>{props.thankYou}</p>}
          </header>
        )}

        {!success && (
          <FormWrap>
            {(props.content &&
              props.content[0] &&
              ((props.content[0].text && props.content[0].text.length > 0) || props.content[0].url)
            ) && <Content content={props.content}/>}

            <form
              action={props.handler}
              method="post"
              onSubmit={e => {
                handleSubmit(e);
              }}
            >
              <FormFields>
                {props.category === 'contact-us' && (
                  <SelectField>
                    <select name="Type__c" required>
                      <option value="" disabled selected>
                        I am a …
                      </option>

                      <option value="Supply">Publisher</option>
                      <option value="Demand">Advertiser</option>
                      <option value="Content">Content Owner</option>
                      <option value="Luminous for Business">Business or Brand</option>
                      <option value="Luminous Watch for Events">Event Organizer</option>
                      <option value="Other">Other</option>
                    </select>
                  </SelectField>
                )}

                <div>
                  <CleanInput type="text" name="First Name" required />
                  <label htmlFor="First Name">First Name*</label>
                </div>

                <div>
                  <CleanInput type="text" name="Last Name" required />
                  <label htmlFor="Last Name">Last Name*</label>
                </div>

                <div>
                  <CleanInput type="text" name="Company" />
                  <label htmlFor="Company">Company</label>
                </div>

                <div>
                  <CleanInput type="email" name="Business Email" required />
                  <label htmlFor="Business Email">Business Email*</label>
                </div>

                <SelectField>
                  <select name="Region" required>
                    <option value="" disabled selected>
                      Select a region
                    </option>
                    <option value="North America">North America</option>
                    <option value="EMEA">Europe, Middle East, Africa</option>
                    <option value="LATAM">Latin America</option>
                    <option value="APAC">Asia-Pacific</option>
                  </select>

                  <label htmlFor="Region">Region*</label>
                </SelectField>

                <SelectField>
                  <select name="Form_Newsletter_Consent" required>
                    <option value="Yes" selected>
                      Yes, please – I want to stay up to date.
                    </option>
                    <option value="No">No, thanks.</option>
                  </select>

                  <label htmlFor="Region">
                    Can we email you about AnyClip's latest products, news or
                    special offers?
                  </label>
                </SelectField>

                <FormCloak>
                  <label htmlFor="pardot_extra_field">Comments</label>
                  <input type="text" id="pardot_extra_field" name="pardot_extra_field"/>
                </FormCloak>
              </FormFields>

              <ButtonRecaptchaContainer>
                <Recaptcha
                  sitekey={
                    props.data.prismic.allSite_settingss.edges[0].node
                      .recaptcha_key || '6LcCRPkUAAAAADWvlZfrh2v4uu4ADF0VLd08_xB-'
                  }
                  render="explicit"
                  onloadCallback={recaptchaLoaded}
                  verifyCallback={verifyCallback}
                />
                {hasError && <div>Please verify you are human</div>}

                <FormButton>
                  {errors.question && <div>{errors.question.message}</div>}

                  {!submitted && (
                    <Button type={button} onClick={verifyYouAreHumanMsg}>
                      <div>{props.buttonText}</div>
                    </Button>
                  )}
                </FormButton>
              </ButtonRecaptchaContainer>
            </form>
          </FormWrap>
        )}
      </Inner>
    </FormContainer>
  );
};

export default props => (
  <StaticQuery
    query={graphql`
      {
        prismic {
          allSite_settingss {
            edges {
              node {
                recaptcha_key
              }
            }
          }
        }
      }
    `}
    render={data => <CondensedContactForm data={data} {...props} />}
  />
);
